import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rejectLoan } from '../../api/loans';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { AssetLoan } from '../../types/api';

export function RejectLoanModal({
  loan,
  open,
  onClose,
}: {
  loan: AssetLoan;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const itemName = loan.asset.orderLine?.customerCatalogItem?.name ?? 'this item';

  const mutation = useMutation({
    mutationFn: () => rejectLoan(loan.id, reason.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan', loan.id] });
      queryClient.invalidateQueries({ queryKey: ['loans-queue'] });
      showToast(`Loan request for "${itemName}" rejected.`);
      setReason('');
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not reject the loan request.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) {
      setError('Please give a reason - the requester will see it.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Reject request for "${itemName}"`}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="reject-loan-form" type="submit" variant="danger" isLoading={mutation.isPending}>
            Reject request
          </Button>
        </>
      }
    >
      <form id="reject-loan-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Input
          label="Reason"
          required
          autoFocus
          hint="Sent to the requester."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </form>
    </Modal>
  );
}
